import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import './AdminLayout.css';

interface AdminLayoutProps {
  children: React.ReactNode;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { user: admin, logout } = useUser();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        dropdownRef.current &&
        buttonRef.current &&
        !dropdownRef.current.contains(target) &&
        !buttonRef.current.contains(target)
      ) {
        setUserDropdownOpen(false);
      }
    };

    if (userDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [userDropdownOpen]);

  // Close sidebar when route changes (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const navItems = [
    {
      path: '/admin/dashboard',
      icon: '🌌',
      label: 'Dashboard',
      active: location.pathname === '/admin/dashboard'
    },
    {
      path: '/admin/products',
      icon: '🌟',
      label: 'Products',
      active: location.pathname.includes('/admin/products')
    },
    {
      path: '/admin/fit-references',
      icon: '📏',
      label: 'Fit Reference',
      active: location.pathname.includes('/admin/fit-references')
    },
    {
      path: '/admin/orders',
      icon: '📦',
      label: 'Orders',
      active: location.pathname.includes('/admin/orders')
    },
    {
      path: '/admin/users',
      icon: '👥',
      label: 'Users',
      active: location.pathname.includes('/admin/users')
    },
    {
      path: '/admin/content',
      icon: '📄',
      label: 'Content',
      active: location.pathname.includes('/admin/content')
    }
  ];

  return (
    <div className="admin-layout">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`admin-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="admin-brand">
            <img src="/header-icons/logo.png" className="brand-logo" alt="EcoFashion logo" />
            <div className="brand-text">
              <h2>EcoFashion</h2>
              <span>Admin Panel</span>
            </div>
          </div>
        </div>

        <nav className="sidebar-nav">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`nav-item ${item.active ? 'active' : ''}`}
            >
              <span className="nav-icon">{item.icon}</span>
              <span className="nav-label">{item.label}</span>
            </Link>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="admin-info">
            <div className="admin-avatar">
              <span>B</span>
            </div>
            <div className="admin-details">
              <p className="admin-name">bbyqueen</p>
              <p className="admin-role">Administrator</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="admin-main">
        {/* Header */}
        <header className="admin-header">
          <div className="header-left">
            <button
              className="mobile-menu-btn"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              aria-label="Toggle menu"
            >
              ☰
            </button>
          </div>

          <div className="header-right">
            <div className="header-stats">
              <div className="stat-item">
                <span className="stat-icon">⏰</span>
                <span className="stat-text">{new Date().toLocaleTimeString('el-GR', {
                  hour: '2-digit',
                  minute: '2-digit'
                })}</span>
              </div>
            </div>

            <div className="admin-dropdown">
              <button
                ref={buttonRef}
                className="admin-profile-btn"
                onClick={() => setUserDropdownOpen(!userDropdownOpen)}
              >
                <div className="profile-avatar">
                  <span>B</span>
                </div>
                <div className="profile-info">
                  <span className="profile-name">bbyqueen</span>
                  <span className="profile-role">Admin</span>
                </div>
                <span className="dropdown-arrow">▼</span>
              </button>

              {userDropdownOpen && (
                <div className="dropdown-menu" ref={dropdownRef}>
                  <div className="dropdown-header">
                    <div className="dropdown-avatar">
                      <span>B</span>
                    </div>
                    <div className="dropdown-info">
                      <p className="dropdown-name">bbyqueen</p>
                      <p className="dropdown-email">{admin?.email}</p>
                    </div>
                  </div>
                  <hr />
                  <button className="dropdown-item">
                    <span className="item-icon">🌟</span>
                    Profile Settings
                  </button>
                  <button className="dropdown-item">
                    <span className="item-icon">🌙</span>
                    System Settings
                  </button>
                  <hr />
                  <button className="dropdown-item logout-item" onClick={handleLogout}>
                    <span className="item-icon">🌠</span>
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="admin-content">
          {children}
        </main>
      </div>

    </div>
  );
};

export default AdminLayout;
